/* eslint-disable no-console */
import { getVideoPosition } from 'Util/assets'
import { getAbsoluteAssetSize } from 'helpers/getAbsoluteAssetSize'
import { isAssetFitsPreviewFrame } from 'helpers/isAssetFitsPreviewFrame'
import { groupBy, isEmpty, isNil } from 'lodash'
import { FILE_TYPE, TRANSITIONS } from '~/enums'
import { calcProjectDuration } from '~/lib'
import { ImageAsset, TextAsset, VideoAsset, MediaAsset, AudioAsset } from '~/models/Asset'
import * as XTL from './XTL'
import { CLIP_FLAGS, TRACK_TYPE, TRANSITION_TRACK_TYPE, XTL_TRANSITION_TYPES } from './enums'
import { isDipTransition } from '~/helpers/assets/isDipTransition'


/**
 * |..l-pad..| clip |..r-pad..|
 * |          range           |
 *
 * @param {Assets.MediaAsset} clip
 * @param {number} totalDuration
 * @returns {{ left: number, right: number }}
 */
function getPadding(clip, totalDuration) {
  return {
    left: Math.max(0, clip.startTime),
    right: Math.max(0, totalDuration - clip.endTime),
  }
}
/**
 * @param {Assets.TransitionAsset[]} transitions
 * @param {Assets.MediaAsset[]} assets
 * @param {TRANSITIONS[]} types
 * @returns {Assets.TransitionAsset[]}
 */
const filterTransitions = ({ transitions, assets, types }) => transitions.reduce(
  (all, current) => {
    const { rightVideoAssetId, leftVideoAssetId } = current
    const assetsSkipCondition = assets && !assets.find(
      asset => asset.id === rightVideoAssetId || asset.id === leftVideoAssetId
    )
    if ((types && !types.includes(current.type)) || assetsSkipCondition) {
      return all
    }
    all.push(current)
    return all
  },
  []
)

/**
* @param {Assets.MediaAsset[]} assets
* @returns {string}
*/
const prepareFileId = asset => {
  const { fileId, type } = asset
  switch (type) {
    case FILE_TYPE.DEVMEDIA:
      return `${FILE_TYPE.DEVMEDIA}_${fileId}`
    default:
      return fileId
  }
}

/**
 * @param {Assets.TransitionAsset[]} transitions
 * @param {number} totalDuration
 * @param {Assets.MediaAsset[]} assets
 * @param {TRANSITIONS[]} transitionTypes
 * @returns {string}
 */
function composeRightVideoTracks(transitions, totalDuration, assets, transitionTypes) {
  const filteredTransitions = filterTransitions({
    transitions,
    assets,
    types: Array.isArray(transitionTypes) ? transitionTypes : [],
  })
  return filteredTransitions.map(transition => {
    const padding = getPadding(transition, totalDuration)
    const assetRef = assets.find(asset => asset.id === transition.rightVideoAssetId)
    if (assetRef === undefined) {
      return '' // in case of image on rightVideoAssetId it handled in pipOverlayTrack
    }
    const { mediaStart, mediaEnd } = assetRef
    const fileId = prepareFileId(assetRef)
    const options = {
      fileId,
      mediaStart,
      mediaEnd,
      padding,
      type: XTL_TRANSITION_TYPES.MIX,
    }
    return XTL.transitionTrack(transition, options)
  }).join('\n')
}

/**
 * @param {Assets.MediaAsset[]} assets
 * @param {number} totalDuration
 * @param {function({asset: Assets.MediaAsset, padding:number[]}):string} xtlTrack
 * @returns {string}
 */
function composeAudioMixTracks(assets, totalDuration) {
  // assume we have already sorted clips here
  return assets.map(asset => {
    const padding = getPadding(asset, totalDuration)
    return XTL.audioTrack({ asset, padding })
  }).join('\n')
}

/**
 * @param {Assets.MediaAsset} media
 * @param {TRACK_TYPE} trackType
 * @returns {string}
 */
export function clipFlags(media, trackType) {
  const isAudioTrackType = [ TRACK_TYPE.VIDEO_AND_AUDIO,
    TRACK_TYPE.AUDIO ].includes(trackType)
  const hasAudio = media.hasAudio || media instanceof AudioAsset
  return [
    ((media.muted || !hasAudio) && isAudioTrackType)
      ? CLIP_FLAGS.AUDIO_SILENCE
      : null,
  ]
    .filter(Boolean)
    .join(',')
}

/**
 * @param {Number} totalDuration
 * @param {Assets.MeidaAsset[]} assets
 * @param {TRACK_TYPE} trackType
 * @param {Assets.TransitionAsset[] } transitions
 * @returns {string}
 */
function composeMediaTrack(trackType, assets, transitions, totalDuration, format, trackFlags) {
  let transitionEffects = []
  const assetsTransitions = filterTransitions({ transitions, assets })
  if (trackType === TRACK_TYPE.VIDEO) {
    transitionEffects = assetsTransitions.map(transition => {
      if (transition.isMountBetween) {
        if (transition.isWipeTransition) {
          return '' // skip effects if transition is wipe one
        }
        if (assets.find(asset => transition.isAttachedToLeft(asset.id)) === undefined) {
          return '' // skip effects on crossing Transitions when leftVideoAssetId is not video
        }
      }
      return XTL.transitionEffectOnTrack(transition, TRANSITION_TRACK_TYPE.ORIGINAL)
    })
  } else if (trackType === TRACK_TYPE.AUDIO) {
    transitionEffects = assets.map(asset => XTL.volumeEffect(asset))
  }

  const mediaClips = []
  if (!assets.length) {
    mediaClips.push(XTL.blankClip(totalDuration, trackType))
  } else {
    let lastTime = 0
    for (const asset of assets) {
      const { mediaStart, mediaEnd } = asset
      const fileId = prepareFileId(asset)
      const clip = { fileId, mediaStart, mediaEnd }
      const flags = clipFlags(asset, trackType)

      const {
        twoSideTransition,
        dipTransitionIn,
        dipTransitionOut,
      } = assetsTransitions.reduce((result, tr) => {
        if (tr.isMountIn && tr.isDipTransition && tr.isAttachedToRight(asset.id)) {
          return { ...result, dipTransitionIn: tr }
        }
        if ((tr.isMountBetween || tr.isMountOut)
          && tr.isDipTransition && tr.isAttachedToLeft(asset.id)) {
          return { ...result, dipTransitionOut: tr }
        }
        if (tr.isMountBetween && tr.isAttachedToRight(asset.id)) {
          return { ...result, twoSideTransition: tr }
        }
        if (tr.isMountBetween && tr.isAttachedToRight(asset.id)) {
          return { ...result, twoSideTransition: tr }
        }
        return result
      }, { dipTransitionIn: null, dipTransitionOut: null, twoSideTransition: null })

      let blankDuration = (asset.startTime - lastTime)
      if (dipTransitionIn) {
        blankDuration += Math.round(dipTransitionIn.duration / 2)
        clip.mediaStart += Math.round(dipTransitionIn.duration / 2)
      } else if (twoSideTransition && (lastTime !== asset.startTime)) {
        const offset = isDipTransition(twoSideTransition.type)
          ? Math.round(twoSideTransition.duration / 2)
          : twoSideTransition.duration
        // this corection needs because assets overlapped http://18.184.210.86/issues/1175#note-17
        blankDuration += offset
        clip.mediaStart += offset
      }
      if (dipTransitionOut) {
        clip.mediaEnd -= Math.round(dipTransitionOut.duration / 2)
      }

      mediaClips.push(XTL.blankClip(blankDuration, trackType))
      mediaClips.push(XTL.mediaClip(clip, flags))

      if (dipTransitionOut) {
        blankDuration = dipTransitionOut.isMountBetween
          ? 0
          : Math.round(dipTransitionOut.duration / 2)
        mediaClips.push(XTL.blankClip(blankDuration, trackType))
      }
      lastTime = asset.endTime
    }
    if (lastTime < totalDuration) {
      mediaClips.push(XTL.blankClip((totalDuration - lastTime), trackType))
    }
  }
  return XTL.mediaTrack(trackType, mediaClips.join('\n'), transitionEffects.join('\n'), format, trackFlags)
}

/**
 * @param {Number} totalDuration
 * @param {Assets.MeidaAsset[]} assets
 * @param { assetId, id, videoPosition } preparedImages
 * @param {Assets.TransitionAsset[] || null} transitions
 * @returns {string}
 */
const composeVideoMixTracks = (
  totalDuration,
  assets,
  preparedImages,
  transitions,
  previewParams,
  refAsset
) => assets.map(
  asset => {
    const assetsTransitions = filterTransitions({
      transitions,
      assets: [ asset ],
    })
    const assetEffects = []
    const transitionEffects = []
    if (asset instanceof VideoAsset) {
      if (asset.settings.isChromaKeyEnabled) {
        assetEffects.push(XTL.chromaKeyEffectOnTrack(asset))
      }
      assetsTransitions.forEach(transition => {
        const trackType = (transition.isMountBetween
          && transition.isAttachedToRight(asset.id))
          ? TRANSITION_TRACK_TYPE.OVERLAY
          : TRANSITION_TRACK_TYPE.ORIGINAL
        transitionEffects.push(XTL.transitionEffectOnTrack(transition, trackType))
      })
    }
    const effects = assetEffects.concat(transitionEffects).join('\n')
    const padding = getPadding(asset, totalDuration)
    let track = ''
    if (!isNil(preparedImages) && !isEmpty(preparedImages)
      && ((asset instanceof TextAsset) || (asset instanceof ImageAsset))) {
      const preparedImage = preparedImages.find(({ assetId }) => assetId === asset.id)
      const { videoPosition, id } = preparedImage
      track = XTL.pipOverlayTrack({ asset, padding, videoPosition, fileId: id, effects })
    } else if (asset instanceof VideoAsset) {
      const { fileId } = asset
      const { offset, rotation } = asset.settings
      const size = getAbsoluteAssetSize(asset, refAsset)
      const { width, height } = size
      const { x, y } = getVideoPosition({ offset, size, rotation, ...previewParams })
      const videoPosition = { x, y, width, height }
      track = XTL.pipOverlayTrack({ asset, padding, videoPosition, fileId, effects })
    }
    return track
  }
).join('\n')

/**
 * @param {TimelineAssetGroups} assets
 * @returns {string}
 */
const hasOutputAudioTrack = (assets, unmutedLayerIds) => {
  const { video, audio } = assets
  const va = video
    .filter(media => unmutedLayerIds.includes(media.layerId) && media.hasAudio && !media.muted)
  const activeAudio = audio
    .filter(media => unmutedLayerIds.includes(media.layerId) && !media.muted)
  return va.length || activeAudio.length
}

/**
 * @param {TimelineAssetGroups} assets
 * @returns {string}
 */
const hasOutputVideoTrack = assets => {
  const { video, image, text } = assets
  return video.length || image.length || text.length
}
/**
 * @param {TimelineAssetGroups} assets
 * @returns {string}
 */
function composeTranscodingTask(
  assetsGroup, assets, { width, height, id: referenceFileId, fpsNum, fpsDenum },
  format, unmutedLayerIds
) {
  // assetsGroup - video asset transformed to audio if it invisible see handleClipsIntersection
  // Don't use assetGroup for refVideo because initial assets are transformed. Use the original assets array. AudioAsset is missing the hasAudio getter.
  const refVideo = referenceFileId && assets.find(x => x.fileId === referenceFileId)
  const params = []
  if (refVideo) {
    params.push(XTL.referenceFileParam(prepareFileId(refVideo)))
  }
  if (!refVideo && hasOutputVideoTrack(assetsGroup)) {
    params.push(XTL.defaultVCodecParams({ width, height, fpsNum, fpsDenum }))
  }
  if ((!refVideo || !refVideo.hasAudio) && hasOutputAudioTrack(assetsGroup, unmutedLayerIds)) {
    params.push(XTL.defaultACodecParams(format))
  }
  return XTL.taskTranscoding(params.join('\n'))
}

function isMixVideoAsset(videoAsset, previewParams, refAsset) {
  return videoAsset.settings.isChromaKeyEnabled
    || !isAssetFitsPreviewFrame(videoAsset, previewParams, refAsset)
}

export const getUnmuted = (assets, unmutedLayerIds) => assets
  .filter(asset => ((asset instanceof MediaAsset && asset.hasAudio)
  || asset instanceof AudioAsset)
  && unmutedLayerIds.includes(asset.layerId) && !asset.muted)

function arrangeAudioAssets(assets, layersIndexes) {
  const grouped = new Map()

  for (const asset of assets) {
    const layerIdx = layersIndexes.get(asset.layerId)
    if (layerIdx === undefined) continue

    if (!grouped.has(layerIdx)) {
      grouped.set(layerIdx, [])
    }
    grouped.get(layerIdx).push(asset)
  }

  return [ ...grouped.entries() ]
    .sort(([ indexA ], [ indexB ]) => indexA - indexB).map(([ _, assets ]) => assets)
}

/**
 * @param {string} outputName
 * @param {string} format
 * @param {TimelineAssetGroups} assetsGroups
 * @param { assetId, id, videoPosition } preparedImages
 * @param {PreviewParams} previewParams
 * @param {TimelineAssets} assets
 * @param {RefVideo} refAsset
 * @param {number[]} layersIndexes
 * @param {number[]} unmutedLayerIds
 * @param {boolean} skipEmptySpace
 * @param {boolean} ignoreQuantizerVideoParamsMismatch
 * @returns {string}
 */
export default function createEditingXTL(
  outputName,
  format,
  assetsGroups,
  assets,
  layersIndexes,
  preparedImages,
  previewParams,
  refAsset,
  unmutedLayerIds,
  skipEmptySpace,
  ignoreQuantizerVideoParamsMismatch
) {
  const { video, image, text, transition, audio } = assetsGroups

  const totalDuration = calcProjectDuration([ video, image, text, audio ].flat(),
    { skipEmptySpace })
  const { ordinaryVideoClips = [], mixVideoClips = [] } = groupBy(video,
    asset => isMixVideoAsset(asset, previewParams, refAsset) ? 'mixVideoClips' : 'ordinaryVideoClips')

  const videoTrackFlags = ignoreQuantizerVideoParamsMismatch ? 'ignore_quantizer_matrix_mismatch' : ''
  const mainVideoTrack = composeMediaTrack(
    TRACK_TYPE.VIDEO,
    ordinaryVideoClips,
    transition,
    totalDuration,
    format,
    videoTrackFlags
  )
  const transitionTracks = composeRightVideoTracks(
    transition.filter(tr => tr.isMountBetween),
    totalDuration,
    ordinaryVideoClips,
    [
      TRANSITIONS.BLUR,
      TRANSITIONS.DISSOLVE,
      TRANSITIONS.WIPE_RIGHT,
      TRANSITIONS.WIPE_LEFT,
      TRANSITIONS.WIPE_UP,
      TRANSITIONS.WIPE_DOWN,
    ]
  )
  const mixVideoAssets = image.concat(text, mixVideoClips).sort(
    (a, b) => layersIndexes.get(b.layerId) - layersIndexes.get(a.layerId)
  )

  const videoMixTracks = composeVideoMixTracks(
    totalDuration, mixVideoAssets, preparedImages, transition, previewParams, refAsset
  )
  let mainAudioTrack = ''
  let audioMixTracks = ''

  const unmuted = getUnmuted(assets, unmutedLayerIds)
  const audioTracks = arrangeAudioAssets(unmuted, layersIndexes)

  if (unmuted.length) {
    mainAudioTrack = composeMediaTrack(
      TRACK_TYPE.AUDIO,
      audioTracks[0],
      transition,
      totalDuration,
      format,
      ''
    ) // TODO: pass transitions when transition type for audio will appear
    if (audioTracks.length > 1) {
      const audioAssets = audioTracks.slice(1)
      audioMixTracks = audioAssets
        .map(audioTrack => composeAudioMixTracks(audioTrack, totalDuration)).join('')
    }
  }

  const tracks = `
    ${mainVideoTrack}
    ${transitionTracks}
    ${videoMixTracks}
    ${mainAudioTrack}
    ${audioMixTracks}
  `
  const transcodingTask = composeTranscodingTask(assetsGroups, assets,
    refAsset, format, unmutedLayerIds)
  return XTL.timelines({
    outputName,
    format,
    transcodingTask,
    tracks,
  })
}
